import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, X } from "@phosphor-icons/react";

export default function EditItemForm({ variant, product, locations, categories, onSaved, onCancel }) {
  const [form, setForm] = useState({
    business_product_id: product?.business_product_id || "",
    product_name: variant.product_name || product?.name || "",
    pack_size: variant.pack_size || "",
    category: product?.category || variant.category || "",
    par_level: variant.par_level ?? "",
    vendor: variant.vendor || "",
    location: variant.location || "",
    units_per_pack: variant.units_per_pack ?? 1,
    cost: variant.cost === null || variant.cost === undefined ? "" : variant.cost,
  });
  const [saving, setSaving] = useState(false);
  const [existingCats, setExistingCats] = useState(categories || []);

  useEffect(() => {
    if (!categories || categories.length === 0) {
      api.get("/items?limit=500").then((r) => {
        const seen = new Map();
        r.data.forEach((v) => {
          const c = String(v.category || "").trim();
          if (!c) return;
          const k = c.toLowerCase();
          if (!seen.has(k)) seen.set(k, c);
        });
        setExistingCats(Array.from(seen.values()).sort((a, b) => a.localeCompare(b)));
      }).catch(() => {});
    }
  }, [categories]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const bpid = String(form.business_product_id || "").trim();
    const upp = Number(form.units_per_pack);
    if (!Number.isFinite(upp) || upp <= 0) return toast.error("Units per Pack must be a positive integer");
    // Cost validation (iter17). Empty string = "Not Set" (null in payload).
    let costPayload = null;
    if (form.cost !== "" && form.cost !== null && form.cost !== undefined) {
      const c = Number(form.cost);
      if (!Number.isFinite(c)) return toast.error("Cost must be a valid number");
      if (c < 0) return toast.error("Cost cannot be negative");
      costPayload = Math.round(c * 100) / 100;
    }
    setSaving(true);
    try {
      const payload = {
        business_product_id: bpid || null,   // blank string -> null
        product_name: String(form.product_name || "").trim(),
        pack_size: String(form.pack_size || "").trim(),
        category: String(form.category || "").trim(),
        par_level: form.par_level === "" || form.par_level === null ? null : Number(form.par_level),
        vendor: String(form.vendor || "").trim(),
        location: String(form.location || "").trim(),
        units_per_pack: upp,
        cost: costPayload,  // null => "Not Set"; number => saved cost
      };
      // Strip nulls except par_level (blank PAR allowed) and business_product_id (blank clears).
      Object.keys(payload).forEach((k) => {
        if (payload[k] === null && k !== "par_level" && k !== "business_product_id") delete payload[k];
      });
      await api.patch(`/items/variants/${variant.variant_id}`, payload);
      toast.success("Item saved");
      onSaved(bpid);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-flat border-2 border-primary p-4 mb-4" data-testid="item-edit-form">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tech">Edit Item Details</div>
        <button type="button" onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground">
          <X size={14} weight="bold" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs">
          <div className="label-tech mb-1">Internal ID <span className="text-muted-foreground normal-case ml-1">(read-only)</span></div>
          <input data-testid="edit-internal-id" value={variant.product_id} readOnly disabled
            className="w-full h-10 px-2 border border-border bg-muted rounded-sm font-mono text-sm text-muted-foreground" />
        </label>
        <label className="text-xs">
          <div className="label-tech mb-1">Product ID <span className="text-muted-foreground normal-case ml-1">(vendor/purchasing ID)</span></div>
          <input data-testid="edit-business-product-id" value={form.business_product_id}
            onChange={(e) => setF("business_product_id", e.target.value)}
            placeholder="e.g. FRANZR (leave blank if unknown)"
            className="w-full h-10 px-2 border border-border bg-background rounded-sm font-mono text-sm" />
        </label>
        <label className="text-xs">
          <div className="label-tech mb-1">Product Name</div>
          <input data-testid="edit-product-name" value={form.product_name} onChange={(e) => setF("product_name", e.target.value)}
            className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
        </label>
        <label className="text-xs">
          <div className="label-tech mb-1">Pack Size</div>
          <input data-testid="edit-pack-size" value={form.pack_size} onChange={(e) => setF("pack_size", e.target.value)}
            placeholder="e.g. #10 Cans, 9.3 Pounds, 5 Liter"
            className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
        </label>
        <label className="text-xs">
          <div className="label-tech mb-1">Category</div>
          <select data-testid="edit-category" value={form.category} onChange={(e) => setF("category", e.target.value)}
            className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm">
            <option value="">(none)</option>
            {existingCats.map((c) => (<option key={c} value={c}>{c}</option>))}
            {form.category && !existingCats.some((c) => c.toLowerCase() === form.category.toLowerCase()) && (
              <option value={form.category}>{form.category}</option>
            )}
          </select>
        </label>
        <label className="text-xs">
          <div className="label-tech mb-1">PAR Level</div>
          <input data-testid="edit-par" type="number" min="0" step="1" value={form.par_level}
            onChange={(e) => setF("par_level", e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="(blank allowed)"
            className="w-full h-10 px-2 border border-border bg-background rounded-sm font-mono text-sm" />
        </label>
        <label className="text-xs">
          <div className="label-tech mb-1">Vendor</div>
          <input data-testid="edit-vendor" value={form.vendor} onChange={(e) => setF("vendor", e.target.value)}
            className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
        </label>
        <label className="text-xs">
          <div className="label-tech mb-1">Location</div>
          <select data-testid="edit-location" value={form.location} onChange={(e) => setF("location", e.target.value)}
            className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm">
            <option value="">(none)</option>
            {(locations || []).map((l) => (<option key={l.location_id} value={l.name || l.location_id}>{l.name || l.location_id}</option>))}
            {form.location && !(locations || []).some((l) => (l.name || l.location_id) === form.location) && (
              <option value={form.location}>{form.location}</option>
            )}
          </select>
        </label>
        <label className="text-xs">
          <div className="label-tech mb-1">Units per Pack</div>
          <input data-testid="edit-units-per-pack" type="number" min="1" step="1" value={form.units_per_pack}
            onChange={(e) => setF("units_per_pack", e.target.value === "" ? "" : Number(e.target.value))}
            className="w-full h-10 px-2 border border-border bg-background rounded-sm font-mono text-sm" />
        </label>
        <label className="text-xs md:col-span-2">
          <div className="label-tech mb-1">Cost <span className="text-muted-foreground normal-case ml-2">(one purchasing pack)</span></div>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <span className="absolute left-2 top-2.5 text-muted-foreground text-sm font-mono">$</span>
              <input data-testid="edit-cost" type="number" min="0" step="0.01" value={form.cost}
                onChange={(e) => setF("cost", e.target.value)}
                placeholder="(leave blank for Not Set)"
                className="w-full h-10 pl-6 pr-2 border border-border bg-background rounded-sm font-mono text-sm" />
            </div>
            {Number(form.cost) > 0 && Number(form.units_per_pack) > 0 && (
              <div className="text-[11px] font-mono text-muted-foreground shrink-0" data-testid="unit-cost-hint">
                Unit Cost: ${(Number(form.cost) / Number(form.units_per_pack)).toFixed(4)}
              </div>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            Enter the cost for one purchasing pack based on the item's Pack Size. Unit Cost is calculated for reference only.
          </div>
        </label>
      </div>
      <div className="flex gap-2 mt-4">
        <button type="submit" data-testid="edit-save" disabled={saving}
          className="btn-hard h-10 px-4 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-orange-600 disabled:opacity-50 flex items-center gap-1">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel}
          className="btn-hard h-10 px-4 border border-border text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-muted">
          Cancel
        </button>
      </div>
    </form>
  );
}
